import axios from "axios";

export const CHUNK_SIZE = 8_000_000;

export const STALL_TIMEOUT = 30_000;

export const MAX_ATTEMPTS = 4;

export const RETRY_BACKOFF = 2_000;

export const upload = {
    /**
     * @param {Number} index
     * @param {Blob} chunk
     * @param {Boolean} isFinalChunk
     * @param {Object} params
     * @returns {*}
     */
    createChunkFormData(index, chunk, isFinalChunk, params) {
        const formData = new FormData();
        formData.append("media", chunk);
        formData.append("order", index);
        formData.append("is_final_chunk", isFinalChunk);

        Object.keys(params).forEach((key) => {
            formData.append(key, params[key]);
        });

        return formData;
    },
    /**
     * @param {string} url
     * @param {File} file
     * @param {Object} params
     * @param {Function} progressCallback
     * @param {AbortController} abortController
     * @returns {Promise<{uploadFailed: Boolean, uploadAborted: Boolean}>}
     */
    async chunks({ url, file, params, progressCallback, abortController }) {
        const totalOfChunks = Math.ceil(file.size / CHUNK_SIZE);

        let sentBytes = 0;

        for (let i = 0; i < totalOfChunks; i++) {
            const isFinalChunk = i + 1 === totalOfChunks;

            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const formData = this.createChunkFormData(
                i,
                chunk,
                isFinalChunk,
                params,
            );

            const { succeeded, aborted } = await this.postChunkWithRetries({
                url,
                formData,
                abortController,
                progressCallback: (loadedOfChunk) => {
                    this.reportProgress(
                        progressCallback,
                        sentBytes + loadedOfChunk,
                        file.size,
                    );
                },
            });

            if (aborted) {
                return { uploadFailed: false, uploadAborted: true };
            }

            if (!succeeded) {
                return { uploadFailed: true, uploadAborted: false };
            }

            sentBytes += end - start;

            this.reportProgress(progressCallback, sentBytes, file.size);
        }

        return { uploadFailed: false, uploadAborted: false };
    },
    /**
     * @param {string} url
     * @param {FormData} formData
     * @param {AbortController} abortController
     * @param {Function} progressCallback
     * @returns {Promise<{succeeded: Boolean, aborted: Boolean}>}
     */
    async postChunkWithRetries({
        url,
        formData,
        abortController,
        progressCallback,
    }) {
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            if (abortController.signal.aborted) {
                return { succeeded: false, aborted: true };
            }

            try {
                await this.postChunk({
                    url,
                    formData,
                    abortController,
                    progressCallback,
                });

                return { succeeded: true, aborted: false };
            } catch (error) {
                if (abortController.signal.aborted) {
                    return { succeeded: false, aborted: true };
                }

                if (attempt < MAX_ATTEMPTS) {
                    await this.delay(RETRY_BACKOFF);
                }
            }
        }

        return { succeeded: false, aborted: false };
    },
    /**
     * @param {string} url
     * @param {FormData} formData
     * @param {AbortController} abortController
     * @param {Function} progressCallback
     * @returns {Promise<*>}
     */
    postChunk({ url, formData, abortController, progressCallback }) {
        const attemptController = new AbortController();

        const abortAttempt = () => attemptController.abort();

        abortController.signal.addEventListener("abort", abortAttempt);

        let stallTimer = null;

        const restartStallTimer = () => {
            clearTimeout(stallTimer);

            stallTimer = setTimeout(abortAttempt, STALL_TIMEOUT);
        };

        restartStallTimer();

        return axios
            .post(url, formData, {
                signal: attemptController.signal,
                onUploadProgress: (event) => {
                    restartStallTimer();

                    progressCallback(event.loaded);
                },
            })
            .finally(() => {
                clearTimeout(stallTimer);

                abortController.signal.removeEventListener(
                    "abort",
                    abortAttempt,
                );
            });
    },
    /**
     * @param {Function} progressCallback
     * @param {Number} sentBytes
     * @param {Number} totalBytes
     * @returns {void}
     */
    reportProgress(progressCallback, sentBytes, totalBytes) {
        if (totalBytes < 1) {
            return;
        }

        const progress = Math.min(
            100,
            Math.round((sentBytes / totalBytes) * 100),
        );

        progressCallback(progress);
    },
    /**
     * @param {Number} milliseconds
     * @returns {Promise<void>}
     */
    delay(milliseconds) {
        return new Promise((resolve) => setTimeout(resolve, milliseconds));
    },
};
